import { DownOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import { IconBuilding } from '@nxsys/components/AppIconSvg';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import apiSearch from '@nxsys/services/search';
import { debounce } from 'lodash';
import { Checkbox, Dropdown, Select, Spin } from 'antd';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

type ApiSearchType = keyof typeof apiSearch;

export type EmployerOption = {
  label: string;
  value: string;
  [key: string]: any;
};

const getOptionLabel = (type: string, item: any): string => {
  switch (type) {
    case 'company':
      return item?.companyName ?? item?.companyPayment?.companyName ?? '';
    case 'agency':
      return item?.name ?? '';
    default:
      return item?.name ?? item?.companyName ?? item?.toString() ?? '';
  }
};

export type AppEmployerMultiSelectProps = {
  options?: EmployerOption[];
  value?: string[];
  onChange?: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** When provided, fetches options from API (like AppSelectSync) */
  apiType?: ApiSearchType;
  /** Type for API response mapping (e.g. 'company' for employers) */
  type?: string;
  paramFilter?: any;
  searchId?: string;
  debounceTimeout?: number;
  isRefetchOnTouch?: boolean;
};

const TriggerButton = styled.div<{ $open: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  min-width: 240px;
  background: ${({ theme }) => theme.palette.background.paper};
  border: 1px solid ${({ theme }) => theme.palette.neutral[300]};
  border-radius: 8px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition:
    border-color 0.2s,
    box-shadow 0.2s;
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};

  ${({ $open, $disabled }) =>
    !$disabled &&
    ($open
      ? `
    border-color: #00795B;
    box-shadow: 0 0 0 1px rgba(0, 121, 91, 0.2);
  `
      : `
    &:hover {
      border-color: #00795B;
    }
  `)}
`;

const DropdownPanel = styled.div`
  min-width: 320px;
  max-width: 400px;
  background: ${({ theme }) => theme.palette.background.paper};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 1px solid ${({ theme }) => theme.palette.neutral[300]};
  padding: 16px;
`;

const Label = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: ${({ theme }) => theme.palette.text.primary};
  margin-bottom: 12px;
`;

const StyledSelect = styled(Select)`
  max-width: 300px;
  min-width: 300px;
  margin-bottom: 12px;

  &.ant-select-open {
    .ant-select-selector {
      border-color: #00795b !important;
      box-shadow: 0 0 0 2px rgba(0, 121, 91, 0.1) !important;
    }
  }

  .ant-select-selector {
    border-radius: 6px !important;
    border-color: ${({ theme }) => theme.palette.neutral[300]} !important;
    padding: 4px 11px !important;

    &:hover {
      border-color: #00795b !important;
    }
  }

  .ant-select-selection-search {
    margin-inline-start: 0 !important;
  }
`;

const OptionsList = styled.div`
  max-height: 240px;
  overflow-y: auto;
  margin-bottom: 16px;
`;

const OptionItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  cursor: pointer;
  border-radius: 4px;
  font-size: 14px;
  color: ${({ theme }) => theme.palette.text.primary};

  &:hover {
    background: ${({ theme }) => theme.palette.neutral[50]};
  }

  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: #00795b !important;
    border-color: #00795b !important;
  }
`;

const FooterButtons = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  padding-top: 12px;
  border-top: 1px solid ${({ theme }) => theme.palette.neutral[100]};
`;

// A shared empty array: a fresh `[]` default would change identity every render,
// so the `[value]` effect below would reset the draft and re-render forever
// (and untick every box) whenever the caller leaves `value` unset, e.g. inside
// an antd Form.Item before the field has a value.
const NO_VALUE: string[] = [];

const AppEmployerMultiSelect: React.FC<AppEmployerMultiSelectProps> = ({
  options: optionsProp,
  value = NO_VALUE,
  onChange,
  placeholder,
  disabled = false,
  className = '',
  apiType,
  type = 'company',
  paramFilter,
  searchId,
  debounceTimeout = 500,
  isRefetchOnTouch = false,
}) => {
  const { messages } = useTypedIntl();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>(value);
  const [search, setSearch] = useState('');
  const [apiOptions, setApiOptions] = useState<EmployerOption[]>([]);
  const [fetching, setFetching] = useState(false);
  const fetchRef = useRef(0);

  const useApi = Boolean(apiType);
  const options = useApi ? apiOptions : optionsProp;

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const debounceFetcher = useMemo(() => {
    if (!apiType || !apiSearch[apiType]) return () => {};

    const searchFn = apiSearch[apiType] as (
      params: any,
      searchId?: string,
    ) => Promise<any>;
    async function fetchOptions(term: string): Promise<any[]> {
      return searchFn(
        { search: term, page: 0, size: 20, ...paramFilter },
        searchId,
      ).then((res: any) => {
        const results = Array.isArray(res?.data?.data) ? res.data.data : [];
        return results;
      });
    }

    const loadOptions = (value: string) => {
      fetchRef.current += 1;
      const fetchId = fetchRef.current;
      setApiOptions([]);
      setFetching(true);

      fetchOptions(value).then((newOptions) => {
        if (fetchId !== fetchRef.current) return;

        setApiOptions(
          newOptions.map((item: any) => ({
            label: getOptionLabel(type, item),
            value: String(item?.id ?? item?.value ?? ''),
            ...item,
          })),
        );
        setFetching(false);
      });
    };

    return debounce(loadOptions, debounceTimeout);
  }, [apiType, type, paramFilter, searchId, debounceTimeout]); // apiType checked above

  useEffect(() => {
    if (useApi && open) {
      if (isRefetchOnTouch) {
        setFetching(true);
        setApiOptions([]);
        debounceFetcher('');
      } else if (apiOptions.length === 0) {
        debounceFetcher(search || '');
      }
    }
  }, [open, useApi, isRefetchOnTouch]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (val: string) => {
    setSearch(val);
    if (useApi) {
      debounceFetcher(val);
    }
  };

  const filteredOptions = useMemo(() => {
    let list = options;
    if (!useApi && search.trim()) {
      const q = search.toLowerCase();
      list =
        options?.filter((opt) => opt.label.toLowerCase().includes(q)) ?? [];
    }
    // Include selected items that may not be in current API results
    if (useApi && draft.length > 0) {
      const optionValues = new Set(list?.map((o) => o.value) ?? []);
      const missingIds = draft.filter((id) => !optionValues.has(id));
      const missingOptions: EmployerOption[] = missingIds.map((id) => ({
        label: id,
        value: id,
      }));
      list = [...missingOptions, ...(list ?? [])];
    }
    return list;
  }, [options, search, useApi, draft]);

  const appliedCount = value.length;

  const triggerLabel =
    appliedCount === 0
      ? `${messages['common.employer'] || 'Employer'}: All`
      : `${messages['common.employer'] || 'Employer'}: ${appliedCount} Employers Applied`;

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setDraft(value);
      setSearch('');
    }
    setOpen(nextOpen);
  };

  const toggleOption = (option: EmployerOption) => {
    const isSelected = draft.includes(option.value);
    setSearch('');
    setDraft((prev) =>
      isSelected
        ? prev.filter((id) => id !== option.value)
        : [...prev, option.value],
    );
  };

  const handleReset = () => {
    setDraft([]);
    onChange?.([]);
    setOpen(false);
  };

  const handleApply = () => {
    onChange?.([...draft]);
    setOpen(false);
  };

  const dropdownContent = (
    <DropdownPanel onClick={(e) => e.stopPropagation()}>
      <Label>{messages['common.employer'] || 'Employer'}</Label>

      <div onClick={(e) => e.stopPropagation()}>
        <StyledSelect
          options={options}
          className="!w-full"
          showSearch
          mode="multiple"
          open={true}
          popupClassName="hidden"
          onSearch={(val) => handleSearch(val)}
          searchValue={search}
          value={draft}
          suffixIcon={null}
          placeholder={
            placeholder ||
            messages['common.selectOneOrMore'] ||
            'Select one or more options'
          }
          onClear={() => {
            setDraft([]);
            onChange?.([]);
            setSearch('');
          }}
          onDeselect={(val) =>
            setDraft(draft.filter((id) => id !== (val as string)))
          }
          allowClear
        />
      </div>

      <OptionsList>
        {fetching ? (
          <div className="flex justify-center py-8">
            <Spin size="small" />
          </div>
        ) : (filteredOptions?.length ?? 0) > 0 ? (
          filteredOptions?.map((opt) => {
            const isChecked = draft.includes(opt.value);
            return (
              <OptionItem key={opt.value} onClick={() => toggleOption(opt)}>
                <Checkbox checked={isChecked} />
                <span>{opt.label}</span>
              </OptionItem>
            );
          })
        ) : (
          <div className="flex justify-center py-8">
            <span className="text-gray-500">{messages['common.empty']}</span>
          </div>
        )}
      </OptionsList>

      <FooterButtons>
        <AppButton
          name={messages['common.reset'] || 'Reset'}
          type="default"
          variant="outlined"
          onClick={handleReset}
          className="!rounded-lg"
        />
        <AppButton
          name={messages['common.apply'] || 'Apply'}
          onClick={handleApply}
          className="!rounded-lg !bg-[#00795B] !border-[#00795B] hover:!bg-[#006B51] hover:!border-[#006B51]"
        />
      </FooterButtons>
    </DropdownPanel>
  );

  return (
    <Dropdown
      open={open}
      onOpenChange={handleOpenChange}
      popupRender={() => dropdownContent}
      trigger={['click']}
      disabled={disabled}
    >
      <TriggerButton $open={open} $disabled={disabled} className={className}>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <IconBuilding width={18} height={18} color="#3E454D" />
          <span className="text-sm truncate">{triggerLabel}</span>
        </div>
        <DownOutlined
          className={`flex-shrink-0 text-gray-500 text-xs transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </TriggerButton>
    </Dropdown>
  );
};

export default AppEmployerMultiSelect;
